// src/features/streams/simulcast/simulcastTransforms.ts

import {
  SimulcastType,
  FBLocationType,
  PrivacyStatus,
  type ISimulcastAllData,
  type ISimulcastRtmpUpdateReq,
  type ISimulcastFBCreateReq,
  type ISimulcastFBUpdateReq,
  type ISimulcastYTCreateReq,
  type ISimulcastYTUpdateReq,
} from '@/lib/types/Simulcast';
import { DESTINATION_TYPE_LABELS, YT_CREATE_DEFAULTS } from '@/lib/constants/simulcast';
import type { RtmpFormData, FBEditFormData, FBCreateFormData, YTCreateFormData, YTEditFormData } from '../schemas/SimulcastForm.schema';

/**
 * Form/request data transforms and display helpers for simulcast destinations.
 */
export class SimulcastTransforms {
  // --- RTMP form data transforms ---

  /** Transform simulcast data to RTMP form data (for edit pre-fill). */
  static transformRtmpToFormData(simulcast: ISimulcastAllData): RtmpFormData {
    return {
      label: simulcast.core.label,
      rtmpServerUrl: simulcast.rtmp?.rtmpServerUrl ?? '',
      rtmpStreamKey: simulcast.rtmp?.rtmpStreamKey ?? '',
      rtmpUser: simulcast.rtmp?.rtmpUser ?? '',
      rtmpPass: simulcast.rtmp?.rtmpPass ?? '',
      retry: simulcast.core.retry,
    };
  }

  /** Transform RTMP form data to API update request. */
  static transformFormToRtmpUpdateReq(data: RtmpFormData): ISimulcastRtmpUpdateReq {
    return {
      label: data.label,
      retry: data.retry,
      rtmpServerUrl: data.rtmpServerUrl,
      rtmpStreamKey: data.rtmpStreamKey,
      rtmpUser: data.rtmpUser || null,
      rtmpPass: data.rtmpPass || null,
    };
  }

  // --- Facebook form data transforms ---

  /** Transform simulcast data to FB edit form data (for edit pre-fill). */
  static transformFBToEditFormData(simulcast: ISimulcastAllData): FBEditFormData {
    return {
      label: simulcast.core.label,
      title: simulcast.core.title ?? '',
      descShort: simulcast.core.descShort ?? '',
    };
  }

  /** Transform FB edit form data to API update request. */
  static transformFBEditFormToUpdateReq(data: FBEditFormData): ISimulcastFBUpdateReq {
    return {
      label: data.label,
      title: data.title || null,
      descShort: data.descShort || null,
    };
  }

  /** Build a FB simulcast create request from location data and form data. */
  static buildFBCreateReq(
    extPlatAuthId: number,
    locationId: string,
    locationLabel: string,
    locationType: FBLocationType,
    pageAccessToken: string | null,
    formData: FBCreateFormData,
  ): ISimulcastFBCreateReq {
    return {
      label: formData.label,
      title: formData.title || null,
      descShort: formData.descShort || null,
      extPlatAuthId,
      fbLocationId: locationId,
      fbLocationLabel: locationLabel,
      fbLocationType: locationType,
      fbPageAccessToken: pageAccessToken,
      fbPrivacyTypeId: null,
      fbPlannedStartTime: null,
      fbSaveVod: false,
      retry: true,
    };
  }

  // --- YouTube form data transforms ---

  /** Transform simulcast data to YT edit form data (for edit pre-fill). */
  static transformYTToEditFormData(simulcast: ISimulcastAllData): YTEditFormData {
    return {
      label: simulcast.core.label,
      title: simulcast.core.title ?? '',
      descShort: simulcast.core.descShort ?? '',
    };
  }

  /** Transform YT edit form data to API update request. */
  static transformYTEditFormToUpdateReq(data: YTEditFormData): ISimulcastYTUpdateReq {
    return {
      label: data.label,
      title: data.title || null,
      descShort: data.descShort || null,
    };
  }

  /** Build a YouTube simulcast create request from auth data and form data. */
  static buildYTCreateReq(
    extPlatAuthId: number,
    formData: YTCreateFormData,
  ): ISimulcastYTCreateReq {
    return {
      label: formData.label,
      title: formData.title || null,
      descShort: formData.descShort || null,
      extPlatAuthId,
      retry: true,
      yt: {
        ytOwnerId: null,
        ytOwnerDisplayName: null,
        ytChannelId: null,
        ytChannelTitle: null,
        ytBrCdEnableDVR: YT_CREATE_DEFAULTS.ytBrCdEnableDVR,
        ytBrCdEnableEmbed: YT_CREATE_DEFAULTS.ytBrCdEnableEmbed,
        ytBrCdEnableAutoStart: YT_CREATE_DEFAULTS.ytBrCdEnableAutoStart,
        ytBrCdEnableAutoStop: YT_CREATE_DEFAULTS.ytBrCdEnableAutoStop,
        ytBrCdEnableClosedCaptions: YT_CREATE_DEFAULTS.ytBrCdEnableClosedCaptions,
        ytBrCdEnableContentEncryption: YT_CREATE_DEFAULTS.ytBrCdEnableContentEncryption,
        ytBrCdRecordFromStart: YT_CREATE_DEFAULTS.ytBrCdRecordFromStart,
        ytBrSnScheduledStartTime: null,
        ytBrStPrivacyStatus: PrivacyStatus.Public,
        ytStreamCdIsReusable: YT_CREATE_DEFAULTS.ytStreamCdIsReusable,
        ytStreamCdnIngestionType: YT_CREATE_DEFAULTS.ytStreamCdnIngestionType,
      },
    };
  }

  // --- Display helpers ---

  /** Get the user-facing label for a simulcast destination. */
  static getDestinationLabel(simulcast: ISimulcastAllData): string {
    return simulcast.core.label || this.getPlatformLabel(simulcast.core.simulcastType);
  }

  /** Get the user-facing platform type label. */
  static getPlatformLabel(type: SimulcastType): string {
    return DESTINATION_TYPE_LABELS[type as keyof typeof DESTINATION_TYPE_LABELS] ?? type;
  }

  /** Get detail line (FB page name, YT channel, RTMP host). */
  static getDestinationDetail(simulcast: ISimulcastAllData): string {
    switch (simulcast.core.simulcastType) {
      case SimulcastType.Facebook:
        return simulcast.fb?.fbLocationLabel ?? 'Facebook';
      case SimulcastType.Youtube:
        return simulcast.yt?.general?.ytChannelTitle ?? simulcast.yt?.general?.ytOwnerDisplayName ?? 'YouTube';
      case SimulcastType.Rtmp: {
        const serverUrl = simulcast.rtmp?.rtmpServerUrl;
        if (!serverUrl) return 'RTMP';
        try {
          return new URL(serverUrl).hostname;
        } catch {
          return serverUrl;
        }
      }
      default:
        return '';
    }
  }
}
